/**
 * The filterable columns of a clause library, and how their values read.
 *
 * The backend declares `__filterable_fields__` as jurisdiction, contract_types,
 * language and clause_category, and returns the distinct values of each in a
 * search's `filter_options`. Only the labels are ours — hence a composable of
 * two computeds rather than a store.
 */
import { computed, type ComputedRef } from 'vue';
import type { FilterConfig } from '@/components/design/composite/GaiaFilterPanel.vue';
import { clauseCategoryMap, contractTypeTemplateKeyMap } from '@/components/design/statusMaps';

export interface UseClauseFilterLabelsReturn {
  filterConfig: ComputedRef<FilterConfig[]>;
  valueLabelMap: ComputedRef<Record<string, Record<string, string>>>;
}

/**
 * The four filterable clause columns.
 *
 * `contract_types` is the one the design's mock does not draw; it comes for
 * free because the panel is driven by whatever `filter_options` returns.
 */
const CLAUSE_FILTER_CONFIG: FilterConfig[] = [
  { key: 'clause_category', label: 'Category' },
  { key: 'contract_types', label: 'Contract types' },
  { key: 'jurisdiction', label: 'Jurisdiction' },
  { key: 'language', label: 'Language' },
];

export function useClauseFilterLabels(): UseClauseFilterLabelsReturn {
  const filterConfig = computed<FilterConfig[]>(() => CLAUSE_FILTER_CONFIG);

  /**
   * Jurisdiction and language are free text the organisation typed, so they
   * read as themselves; only the two enum columns have labels to resolve. An
   * organisation library's contract types are already names, so only the GAIA
   * library's template keys need the map; anything unmapped reads as itself.
   */
  const valueLabelMap = computed<Record<string, Record<string, string>>>(() => ({
    clause_category: { ...clauseCategoryMap },
    contract_types: { ...contractTypeTemplateKeyMap },
  }));

  return { filterConfig, valueLabelMap };
}
